import firebase from 'firebase';
import { createDits, switchDitsToUser } from '../../helpers/apiHelper';
import { FirestoreHelper } from '../../helpers/firestoreHelper';
import { COLLECTIONS, COURSES, DINE_IN_TABLE_SITTINGS_MENU_ITEMS_STATUS, DISH_CATEGORY_TO_COURSE, DITS_STATUS } from '../../constants/data';
import { MenuItemAddOn, TimeSlot, UpdateTimeSlot } from '../../models';
import { DeliveryDetails, DeliveryDetailsUpdate, ServiceType, Session, SessionMenuItem } from '../../models/session';
import { LocalizerHelper } from '../../helpers/localizerHelper';
import { MenuItem, Minus } from '../../models/menuItem';
import { firestore } from '../../firebase';
import { handleRefOrString } from '../../helpers/genericHelper';
import { serializeAddOns, serializeMinus } from '../menu/menu.service';
import { DeliveryMeasureType } from '../../models/restaurant';

export const serializeSessionModelSnap = (session: firebase.firestore.DocumentSnapshot): Session => {
  return serializeSessionModel(session.id, session.data() ?? {});
};

export const serializeSessionModel = (id: string, data: {[key: string]: any}): Session => ({
  id: id,
  code: data.code ?? "",
  allowed_payment_methods: data.allowed_payment_methods,
  delivery_mode: data.delivery_mode,
  delivery_details: serializeDeliveryDetails(data.delivery_details),
  must_pay_first: data.must_pay_first ?? false,
  payment_method: data.payment_method ?? null,
  service_type: serializeServiceType(data.service_type),
  status: data.status ?? DITS_STATUS.started,
  table_name: data.table_name ?? "",
  time_slot: serializeSessionTimeSlot(data.time_slot),
});

const serializeSessionTimeSlot = (data: {[key: string]: any} | null): TimeSlot|null => {
  if(!data) return null;
  return {
    end: data.end?.toMillis(),
    start: data.start?.toMillis(),
    offset: data.offset,
  }
}

const serializeServiceType = (data: {[key: string]: any} | null): ServiceType|null => {
  if(!data) return null;
  return {
    has_time_slots: data.has_time_slots ?? true,
    request_user_data: data.request_user_data ?? false,
  }
}

const serializeDeliveryDetails = (data: {[key: string]: any}): DeliveryDetails|null => {
  if(!data) return null;
  return {
    address: data.address ?? "",
    delivery_measure_type: data.delivery_measure_type ?? DeliveryMeasureType.KM,
    distance: data.distance ?? 0,
    doorbell_name: data.doorbell_name ?? "",
    notes: data.notes ?? "",
    phone_number: data.phone_number ?? "",
    preview_delivery_cost: data.preview_delivery_cost ?? 0,
  }
}

export const serializeSessionMenuItemModelSnap = (sessionMenuItem: firebase.firestore.DocumentSnapshot): SessionMenuItem => {
  return serializeSessionMenuItemModel(sessionMenuItem.ref, sessionMenuItem.data() ?? {});
};

export const serializeSessionMenuItemModel = (ref: firebase.firestore.DocumentReference, data: {[key: string]: any}): SessionMenuItem => ({
  add_ons: (data.add_ons ?? []).map(serializeAddOns),
  course: data.course ?? COURSES.asap,
  id: ref.id,
  menu_item: handleRefOrString(data.menu_item),
  meta: data.meta ?? {},
  minus: (data.minus ?? []).map(serializeMinus),
  name: data.name ?? '',
  note: data.note ?? "",
  price: data.price ?? 0,
  ref: ref.path,
  sale_comment: data.sale_comment ?? '',
  sales_model: data.sales_model ?? '',
  prepare_weight: data.prepare_weight ?? null,
});

export const createNewSessionService = async (
  linkId: string
): Promise<string> => {
  const data = await createDits(linkId);
  const sessionID = data?.dits;
  return sessionID;
};

export const updateDeliveryDetails = async (
  restaurantId: string,
  sessionId: string,
  payload: firebase.firestore.DocumentData
) => {
  return firebase
    .firestore()
    .collection(COLLECTIONS.restaurants)
    .doc(restaurantId)
    .collection(COLLECTIONS.dineInTableSitting)
    .doc(sessionId)
    .update({
      delivery_details: payload,
      updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    });
};

export const updateTimeSlot = async (
  restaurantId: string,
  sessionId: string,
  slot: UpdateTimeSlot
) => {
  return firebase
    .firestore()
    .collection(COLLECTIONS.restaurants)
    .doc(restaurantId)
    .collection(COLLECTIONS.dineInTableSitting)
    .doc(sessionId)
    .update({
      time_slot: slot
    });
};

export const updatePaymentMethod = async (
  restaurantId: string,
  sessionId: string,
  paymentMethod: string
) => {
  return firebase
    .firestore()
    .collection(COLLECTIONS.restaurants)
    .doc(restaurantId)
    .collection(COLLECTIONS.dineInTableSitting)
    .doc(sessionId)
    .update({
      payment_method: paymentMethod
    });
};

export const addItemToSession = async (
  restaurantId: string,
  sessionId: string,
  uid: string,
  menuItemPath: string,
  menuItem: MenuItem, 
  addOns: MenuItemAddOn[] = [], 
  minus: Minus[] = [], 
  notes: string = '', 
  qty: number = 1
) => {
  console.log("addItemsToSession");
  const restaurantRef = firestore.collection(COLLECTIONS.restaurants).doc(restaurantId);
  const ditsRef = restaurantRef.collection(COLLECTIONS.dineInTableSitting).doc(sessionId);
  const userRef = firestore.collection(COLLECTIONS.users).doc(uid);
  const ditsMenuItem: firebase.firestore.DocumentData = {
    ...menuItem,
    add_ons: addOns ?? [],
    name: LocalizerHelper.localized(menuItem, ['name']),
    minus: minus ?? [],
    menu_item: firestore.doc(menuItemPath),
    course: DISH_CATEGORY_TO_COURSE[menuItem.category] ?? COURSES.asap,
    status: DINE_IN_TABLE_SITTINGS_MENU_ITEMS_STATUS.chosen,
    note: notes,
    sale_comment: "",
    ordered_at: null,
    chosen_at: firebase.firestore.Timestamp.now(),
    chosen_by: userRef,
  }
  delete ditsMenuItem.category;
  delete ditsMenuItem.diets;
  delete ditsMenuItem.ingredients;
  try{
    if(!!ditsMenuItem.tax_rate && ditsMenuItem.tax_rate instanceof firebase.firestore.DocumentReference){
      const taxRateSnap = await ditsMenuItem.tax_rate.get();
      if(!!taxRateSnap && taxRateSnap.exists){
        const tax_rate = taxRateSnap.data()?.value;
        console.log(`tax_rate: ${tax_rate}`);
        ditsMenuItem.tax_rate = tax_rate;
      }
    }
  }catch(e){
    console.log("error in get tax_rate");
    console.log(e);
  }
  try {
    await FirestoreHelper.addItemsToSession(ditsRef, ditsMenuItem, qty);
  } catch (error) {
    console.error(error);
  }
}

export const deleteItemFromSession = async (item: SessionMenuItem) => {
  try {
    const ref = firestore.doc(item.ref);
    await FirestoreHelper.deleteItemFromSession(ref);
  } catch (error) {
      console.log(error);
  }
}

export const updateDeliveryInfo = async (
  restaurantId: string,
  sessionId: string,
  deliveryDetails: DeliveryDetailsUpdate
) => {
  return firebase
    .firestore()
    .collection(COLLECTIONS.restaurants)
    .doc(restaurantId)
    .collection(COLLECTIONS.dineInTableSitting)
    .doc(sessionId)
    .update({
      "delivery_details.address": deliveryDetails.address,
      "delivery_details.doorbell_name": deliveryDetails.doorbell_name,
      "delivery_details.notes": deliveryDetails.notes,
      "delivery_details.phone_number": deliveryDetails.phone_number,
    });
};

export const switchSessionToUser = async (anonymousUid: string) => {
  try{
    const response = await switchDitsToUser(anonymousUid);
    return response;
  }catch(e){
    alert(e);
  }
}

